/**
 * Top of the focus band, as a percentage of the scroll container's height.
 * The scroll-driven IntersectionObserver (scrollable-content.vue) derives its
 * rootMargin from this, and `itemIndexInFocusBand` resolves the item crossing
 * the same line — one definition of "the item the user is looking at".
 */
export const FOCUS_BAND_TOP_PERCENT = 10

/**
 * Holds the review item the user is looking at steady in the scroll container
 * while asynchronous batch loads grow the content above it.
 *
 * Review items render as short placeholders until their batch loads, then
 * expand to full height — and that expansion can land several frames after the
 * data arrives. A `ResizeObserver` on the item-list wrapper (passed in, since
 * it is not always the container's first child — compare mode prepends a
 * fixed-height header) watches for any height change and simply re-pins the
 * item, so it holds however late and however many times the layout grows.
 *
 * The pin target is set two ways: an explicit navigation (`scrollToIndex`)
 * pins the item to the top; a scroll/hover that focuses an item (`refocus`)
 * pins it wherever the user's scroll left it. Either way the target is
 * re-resolved by `data-index` on every pin. It deliberately ignores focus
 * *drift* — the scroll-driven IntersectionObserver re-focusing whatever sits at
 * the top while we re-pin mid-settle — since anchoring to that moving target
 * would pin the wrong element. Re-querying also survives the item's own node
 * being replaced when its batch renders.
 *
 * Distinguishing our own scroll writes from the user's is done by value, not by
 * time: every programmatic write records the resulting `scrollTop`, so a scroll
 * event landing on a different value is the user taking over. Once the user
 * scrolls, we stop pinning the target, so a late batch never pulls them back to
 * it. Each user scroll instead records the item in the focus band, and a resize
 * moves `scrollTop` by as much as that item moved. Without this hold, a long
 * template that loads above the viewport pushes the page down by its full
 * height. A deliberate navigation (`scrollToIndex()`) or the focus of the item
 * their scroll landed on (`refocus()`) makes that item the new pin target.
 *
 * The container must have `overflow-anchor: none` so the browser's native
 * scroll anchoring does not also move `scrollTop` and race with this logic.
 */
export class FocusedScrollAnchor {
  private expectedScrollTop = 0
  private userScrolledAway = false
  private targetIndex: number | null = null
  private targetOffset = 0
  private scrollHoldIndex: number | null = null
  // `offsetTop` of the held item (the target, or the scroll hold once the user
  // scrolled away) when we last measured or positioned it.
  private heldItemTop: number | null = null
  private resizeObserver: ResizeObserver

  constructor(private root: HTMLElement, content: HTMLElement | null) {
    this.resizeObserver = new ResizeObserver(() => this.reanchor())
    if (content) this.resizeObserver.observe(content)
  }

  /**
   * Pin the item with the given `data-index` to the top of the container. Call
   * once the item has rendered (e.g. after Vue's nextTick). A navigation means
   * we own the scroll position again, so this clears the user-scroll latch.
   */
  scrollToIndex(index: number): void {
    this.targetIndex = index
    this.targetOffset = 0
    this.userScrolledAway = false
    this.heldItemTop = null
    this.pin()
  }

  /**
   * Wire to the container's scroll event. Our own writes land on
   * `expectedScrollTop`; any other value is the user scrolling, which stops
   * pinning the target until the next `scrollToIndex()` or `refocus()`.
   */
  handleScroll(): void {
    this.latchIfUserScrolled()
  }

  /**
   * Adopt a newly focused item as the pin target, but only when the focus is
   * the result of a real user scroll (the latch is set). This keeps the item
   * the user scrolled to stable while batches above it grow — the same
   * stability native scroll anchoring used to provide, which `overflow-anchor:
   * none` turned off.
   *
   * Unlike a navigation, this holds the item at whatever offset the user's
   * scroll left it at — it does not yank it to the top. Snapping a scrolled-to
   * item to the top would shift the rest of its row out of the position the user
   * (or a test scrolling to a child link) chose.
   *
   * When the latch is clear the focus change is our own doing: the scroll-driven
   * IntersectionObserver re-focuses whatever drifts to the top while we re-pin a
   * navigated item mid-settle. Ignoring those keeps a navigation's target from
   * being stolen by a neighbour.
   */
  refocus(index: number): void {
    if (!this.userScrolledAway) return

    this.anchorAtCurrentOffset(index)
  }

  /**
   * Hold the given item at its current on-screen position across an imminent
   * layout reflow — e.g. a side drawer (file preview, communication, audit log)
   * opening or closing narrows/widens the list column, so the fluid liquid
   * content reflows to a different height. Call this *synchronously before* the
   * reflow, while the DOM still has the pre-reflow geometry: it snapshots the
   * item's offset from the container top and pins it there, so the
   * `ResizeObserver` holds it steady as the content resizes around it.
   *
   * Unlike `refocus`, it ignores the user-scroll latch. A drawer toggle is an
   * external layout change, never the user scrolling, so we always want to keep
   * the item they are looking at in place. Without this, `overflow-anchor: none`
   * lets content grow above a frozen `scrollTop`, drifting the focused item down
   * until the scroll-driven IntersectionObserver hands focus (and the URL and
   * sidebar) to whatever neighbour reaches the top band.
   */
  holdFocusedItem(index: number): void {
    this.anchorAtCurrentOffset(index)
  }

  /**
   * True while we are holding a pinned target the user has not scrolled away
   * from (a navigation or the initial URL-focused item). In that state any
   * focus change reported by the scroll-driven IntersectionObserver is drift —
   * a neighbour reaching the top band as content grows above the target, not a
   * user action — and the caller should ignore it so the target keeps the
   * focus, URL and sidebar selection.
   *
   * Reads the container's scroll position itself instead of waiting for
   * `handleScroll()` to set `userScrolledAway`. The observer can report a
   * focus change while that flag is still false, and the hold would then
   * ignore the focus change the user asked for.
   */
  isHoldingTarget(): boolean {
    if (this.targetIndex === null || this.userScrolledAway) return false

    this.latchIfUserScrolled()

    return !this.userScrolledAway
  }

  /**
   * The index of the item currently crossing the focus band — the same top
   * band the scroll-driven IntersectionObserver uses to assign focus — read
   * directly from the DOM. Used to pick the hold target for a reflow that is
   * about to happen (a drawer opening or closing), where the component's
   * focused index may lag the user's scroll during async loads: the DOM is the
   * fact. Falls back to the closest item above the band (the band can sit in
   * the list's bottom padding); null when no item qualifies.
   */
  itemIndexInFocusBand(): number | null {
    const rootRect = this.root.getBoundingClientRect()
    const bandY =
      rootRect.top + (rootRect.height * FOCUS_BAND_TOP_PERCENT) / 100

    let closestAbove: number | null = null
    let closestAboveTop = -Infinity
    const items = Array.from(
      this.root.querySelectorAll<HTMLElement>('[data-index]')
    )
    for (const el of items) {
      const rect = el.getBoundingClientRect()
      if (rect.top > bandY) continue

      const index = Number(el.dataset.index)
      if (Number.isNaN(index)) continue

      if (rect.bottom > bandY) return index
      if (rect.top >= closestAboveTop) {
        closestAboveTop = rect.top
        closestAbove = index
      }
    }
    return closestAbove
  }

  destroy(): void {
    this.resizeObserver.disconnect()
  }

  private latchIfUserScrolled(): void {
    if (Math.abs(this.root.scrollTop - this.expectedScrollTop) <= 1) return

    // A scroll landing on the pin position is not the user taking over. It is
    // the item's own mount-time scroll-into-view (workflow-review-item sets
    // `parentEl.scrollTop = offsetTop`) or a layout settle, and on a slow
    // initial load it can arrive before scrollToIndex re-bases
    // expectedScrollTop. Adopt it as the expected position instead of setting
    // `userScrolledAway`.
    const el = this.targetElement()
    if (
      el &&
      Math.abs(this.root.scrollTop - (el.offsetTop - this.targetOffset)) <= 1
    ) {
      this.expectedScrollTop = this.root.scrollTop
      return
    }

    this.followHeldItem()
    this.userScrolledAway = true
    this.recordScrollHold()
  }

  private recordScrollHold(): void {
    this.scrollHoldIndex = this.itemIndexInFocusBand()
    const el =
      this.scrollHoldIndex === null
        ? null
        : this.itemElement(this.scrollHoldIndex)
    this.heldItemTop = el ? el.offsetTop : null
  }

  // Move `scrollTop` by as much as the held item moved since we last measured
  // it. Call this before taking a new measurement: a batch's HTML can be in the
  // DOM before its ResizeObserver callback runs, and a scroll event or an
  // IntersectionObserver task that reads the layout in between would otherwise
  // take the moved position as the one to hold, and the growth stays
  // uncorrected.
  private followHeldItem(): void {
    const index = this.userScrolledAway
      ? this.scrollHoldIndex
      : this.targetIndex
    const el = index === null ? null : this.itemElement(index)
    if (!el || this.heldItemTop === null) {
      return
    }

    const shift = el.offsetTop - this.heldItemTop
    this.heldItemTop = el.offsetTop
    if (shift !== 0) {
      this.setScrollTop(this.root.scrollTop + shift)
    }
  }

  // Adopt `index` as the pin target, held at whatever offset from the container
  // top it currently sits at (not yanked to the top), and clear the user-scroll
  // latch so subsequent resizes re-pin it. Shared by refocus (guarded by the
  // latch) and holdFocusedItem (unconditional).
  private anchorAtCurrentOffset(index: number): void {
    this.followHeldItem()

    const el = this.itemElement(index)
    if (!el) return

    this.targetIndex = index
    this.targetOffset = el.offsetTop - this.root.scrollTop
    this.userScrolledAway = false
    this.pin()
  }

  // Re-pin the target item to its anchored offset after the content changed
  // size. Once the user has scrolled away, hold the item their last scroll left
  // in the focus band instead.
  private reanchor(): void {
    if (this.userScrolledAway) {
      this.followHeldItem()
      return
    }

    this.pin()
  }

  // Hold the target item `targetOffset` px below the container top: 0 for a
  // navigation (item at the top), or wherever the user's scroll left it for a
  // refocus.
  private pin(): void {
    const el = this.targetElement()
    if (!el) return

    this.setScrollTop(el.offsetTop - this.targetOffset)
    this.heldItemTop = el.offsetTop
  }

  private targetElement(): HTMLElement | null {
    if (this.targetIndex === null) return null

    return this.itemElement(this.targetIndex)
  }

  private itemElement(index: number): HTMLElement | null {
    return this.root.querySelector(`[data-index="${index}"]`)
  }

  private setScrollTop(value: number): void {
    this.root.scrollTop = value
    this.expectedScrollTop = this.root.scrollTop
  }
}
